"use client";

import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../../lib";
import { SpeechBubble } from "../../custom/speech-bubble";
import {
	contentPointToViewportPoint,
	isPointInsideViewport,
	resolveViewportEdgePointAlongRay,
} from "../../pan-zoom/pan-zoom-math";
import {
	SCOPED_PANEL_MAP_SPOTLIGHT_SPEECH_Z_INDEX,
	SCOPED_PANEL_SPOTLIGHT_DATA_ATTR,
	scopedPanelSpotlightClassName,
} from "../../scoped-panel/constants";
import {
	type CharacterMovementDisplaySnapshot,
	resolveCharacterRenderCenter,
} from "../hooks/use-hexagon-map-character-movement";

/** Inset from the map viewport edge for the finder bubble anchor (px). */
const HEXAGON_MAP_PLAYER_FINDER_EDGE_INSET_PX = 50;
const HEXAGON_MAP_PLAYER_FINDER_BUBBLE_SIZE_PX = 58;

interface FinderLayout {
	characterCenter: { left: number; top: number };
	edgePoint: { x: number; y: number };
	arrowRotationDeg: number;
}

interface HexagonMapPlayerFinderProps {
	playerAvatarSrc: string;
	viewportWidth: number;
	viewportHeight: number;
	contentWidth: number;
	contentHeight: number;
	pan: { x: number; y: number };
	scale: number;
	characterViewportMargin: number;
	subscribeDisplay: (listener: () => void) => () => void;
	getDisplaySnapshot: () => CharacterMovementDisplaySnapshot;
	restingCenter: { left: number; top: number } | null;
	isCharacterVisible: boolean;
	onPanToCharacter: (contentCenter: { left: number; top: number }) => void;
	/** When stop preview dim is active, keep the finder above the overlay. */
	isScopedPanelOpen?: boolean;
}

function resolveFinderLayout(args: {
	isCharacterVisible: boolean;
	characterCenter: { left: number; top: number } | null;
	viewportWidth: number;
	viewportHeight: number;
	contentWidth: number;
	contentHeight: number;
	pan: { x: number; y: number };
	scale: number;
	characterViewportMargin: number;
}): FinderLayout | null {
	const {
		isCharacterVisible,
		characterCenter,
		viewportWidth,
		viewportHeight,
		contentWidth,
		contentHeight,
		pan,
		scale,
		characterViewportMargin,
	} = args;
	if (
		!isCharacterVisible ||
		!characterCenter ||
		viewportWidth <= 0 ||
		viewportHeight <= 0
	) {
		return null;
	}

	const characterViewport = contentPointToViewportPoint({
		contentPoint: { x: characterCenter.left, y: characterCenter.top },
		contentWidth,
		contentHeight,
		viewportWidth,
		viewportHeight,
		pan,
		scale,
	});

	if (
		isPointInsideViewport(
			characterViewport,
			viewportWidth,
			viewportHeight,
			characterViewportMargin,
		)
	) {
		return null;
	}

	const viewportCenter = {
		x: viewportWidth / 2,
		y: viewportHeight / 2,
	};
	const inset = {
		left: HEXAGON_MAP_PLAYER_FINDER_EDGE_INSET_PX,
		top: HEXAGON_MAP_PLAYER_FINDER_EDGE_INSET_PX,
		right: viewportWidth - HEXAGON_MAP_PLAYER_FINDER_EDGE_INSET_PX,
		bottom: viewportHeight - HEXAGON_MAP_PLAYER_FINDER_EDGE_INSET_PX,
	};
	const edgePoint = resolveViewportEdgePointAlongRay({
		from: viewportCenter,
		toward: characterViewport,
		inset,
	});
	if (!edgePoint) {
		return null;
	}

	const arrowRotationDeg =
		(Math.atan2(
			characterViewport.y - edgePoint.y,
			characterViewport.x - edgePoint.x,
		) *
			180) /
		Math.PI;

	return {
		characterCenter,
		edgePoint,
		arrowRotationDeg,
	};
}

export const HexagonMapPlayerFinder = memo(function HexagonMapPlayerFinder({
	playerAvatarSrc,
	viewportWidth,
	viewportHeight,
	contentWidth,
	contentHeight,
	pan,
	scale,
	characterViewportMargin,
	subscribeDisplay,
	getDisplaySnapshot,
	restingCenter,
	isCharacterVisible,
	onPanToCharacter,
	isScopedPanelOpen = false,
}: HexagonMapPlayerFinderProps) {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const characterCenterRef = useRef<{ left: number; top: number } | null>(
		null,
	);
	const edgePointRef = useRef<{ x: number; y: number } | null>(null);
	const layoutVisibleRef = useRef(false);
	const [isOffscreen, setIsOffscreen] = useState(false);
	const [arrowRotationDeg, setArrowRotationDeg] = useState(0);

	const viewportWidthRef = useRef(viewportWidth);
	const viewportHeightRef = useRef(viewportHeight);
	const contentWidthRef = useRef(contentWidth);
	const contentHeightRef = useRef(contentHeight);
	const panRef = useRef(pan);
	const scaleRef = useRef(scale);
	const marginRef = useRef(characterViewportMargin);
	const restingCenterRef = useRef(restingCenter);
	const isCharacterVisibleRef = useRef(isCharacterVisible);
	viewportWidthRef.current = viewportWidth;
	viewportHeightRef.current = viewportHeight;
	contentWidthRef.current = contentWidth;
	contentHeightRef.current = contentHeight;
	panRef.current = pan;
	scaleRef.current = scale;
	marginRef.current = characterViewportMargin;
	restingCenterRef.current = restingCenter;
	isCharacterVisibleRef.current = isCharacterVisible;

	useEffect(() => {
		const apply = () => {
			const characterCenter = resolveCharacterRenderCenter(
				getDisplaySnapshot(),
				restingCenterRef.current,
			);
			const layout = resolveFinderLayout({
				isCharacterVisible: isCharacterVisibleRef.current,
				characterCenter,
				viewportWidth: viewportWidthRef.current,
				viewportHeight: viewportHeightRef.current,
				contentWidth: contentWidthRef.current,
				contentHeight: contentHeightRef.current,
				pan: panRef.current,
				scale: scaleRef.current,
				characterViewportMargin: marginRef.current,
			});
			characterCenterRef.current = layout?.characterCenter ?? null;
			edgePointRef.current = layout?.edgePoint ?? null;
			const nextVisible = layout != null;
			if (layoutVisibleRef.current !== nextVisible) {
				layoutVisibleRef.current = nextVisible;
				setIsOffscreen(nextVisible);
			}
			if (!layout) {
				return;
			}
			const el = buttonRef.current;
			if (el) {
				el.style.left = `${layout.edgePoint.x}px`;
				el.style.top = `${layout.edgePoint.y}px`;
			}
			setArrowRotationDeg((prev) =>
				Math.abs(prev - layout.arrowRotationDeg) < 0.5
					? prev
					: layout.arrowRotationDeg,
			);
		};
		apply();
		return subscribeDisplay(apply);
	}, [
		getDisplaySnapshot,
		subscribeDisplay,
		viewportWidth,
		viewportHeight,
		contentWidth,
		contentHeight,
		pan,
		scale,
		characterViewportMargin,
		restingCenter,
		isCharacterVisible,
	]);

	useLayoutEffect(() => {
		if (!isOffscreen) {
			return;
		}
		const edge = edgePointRef.current;
		const el = buttonRef.current;
		if (edge && el) {
			el.style.left = `${edge.x}px`;
			el.style.top = `${edge.y}px`;
		}
	}, [isOffscreen]);

	if (!isOffscreen) {
		return null;
	}

	const edge = edgePointRef.current;

	return (
		<button
			ref={buttonRef}
			type="button"
			{...(isScopedPanelOpen
				? { [SCOPED_PANEL_SPOTLIGHT_DATA_ATTR]: "player-finder" }
				: {})}
			className={cn(
				"pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2",
				isScopedPanelOpen && scopedPanelSpotlightClassName,
				"cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2",
			)}
			style={{
				left: edge?.x ?? 0,
				top: edge?.y ?? 0,
				zIndex: isScopedPanelOpen
					? SCOPED_PANEL_MAP_SPOTLIGHT_SPEECH_Z_INDEX
					: 35,
			}}
			aria-label="Pan to your character"
			onPointerDown={(event) => {
				event.stopPropagation();
			}}
			onClick={(event) => {
				event.stopPropagation();
				const center = characterCenterRef.current;
				if (center) {
					onPanToCharacter(center);
				}
			}}
		>
			<SpeechBubble
				imageSrc={playerAvatarSrc}
				imageAlt="Your character"
				variant="light"
				sizePx={HEXAGON_MAP_PLAYER_FINDER_BUBBLE_SIZE_PX}
				arrowRotationDeg={arrowRotationDeg}
				enableFloat={false}
			/>
		</button>
	);
});
